<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'

import EventBus from '../../../../event-bus/EventBus'
import { useAdaMoltenBundle } from '../../../../composables/use-ada-molten-bundle'
import { isValidPositiveDecimalInput, preventNonPositiveNumberKeys } from '../../../../utils/validation/number'
import { useDeviceDetection } from '../../../../composables/use-device-detection'
import { useConfigStore } from '../../../../stores/config'
import Icon from '../../../../components/shared/Icon.vue'

type Gender = 'male' | 'female'
const alcoholTypes = ['beer', 'wine', 'liqueur', 'schnapps'] as const
type AlcoholType = typeof alcoholTypes[number]
type InputValue = number | string
type Person = { gender: Gender | '', age: InputValue, height: InputValue, weight: InputValue }
type Alcohol = Record<AlcoholType, InputValue>
type ErrorField = 'gender' | 'age' | 'height' | 'weight' | AlcoholType | 'duration'
type Errors = Record<ErrorField | 'required' | 'number', boolean>

const alcoholVolume: Record<AlcoholType, number> = { beer: 5, wine: 10, liqueur: 25, schnapps: 40 }
const requiredFields: ErrorField[] = ['gender', 'age', 'height', 'weight', 'duration']

const initErrors = (): Errors => ({
  required: false,
  number: false,
  gender: false,
  age: false,
  height: false,
  weight: false,
  beer: false,
  wine: false,
  liqueur: false,
  schnapps: false,
  duration: false
})

const configStore = useConfigStore()
const { deviceType } = useDeviceDetection()
const registerSlots = ref<string[]>([])
const { adaSlotReloadOnInteraction } = useAdaMoltenBundle({ registerSlots, deviceType })

const person = reactive<Person>({ gender: '', age: '', height: '', weight: '' })
const alcohol = reactive<Alcohol>({ beer: '', wine: '', liqueur: '', schnapps: '' })
const duration = ref<InputValue>('')
const result = ref('')
const resultHidden = ref(true)
const hasError = ref(false)
const errors = ref<Errors>(initErrors())

const reloadConfig = computed(() => ({
  ...configStore.rsConfig.adSlotUsage?.reloadAdsOnInteraction?.config,
  minDisplayDuration: 0,
  clickCount: 1
}))

const hideResult = () => {
  resultHidden.value = true
}

watch([person, alcohol, duration], hideResult, { deep: true })

const calculateAlcohol = () => {
  const alcoholDensity = 0.08
  const { gender } = person
  const age = Number(person.age)
  const height = Number(person.height)
  const weight = Number(person.weight)

  const alcoholGrams = alcoholTypes.reduce((sum, key) => {
    const amount = Number(alcohol[key])
    if (!amount || amount <= 0) return sum
    if (key === 'beer' || key === 'wine') return sum + amount * 100 * alcoholVolume[key] * alcoholDensity
    return sum + amount * alcoholVolume[key] * alcoholDensity
  }, 0)

  const totalBodyWater = gender === 'male'
    ? 2.447 - 0.09156 * age + 0.1074 * height + 0.3362 * weight
    : 0.203 - 0.07 * age + 0.1069 * height + 0.2466 * weight

  const resorption = 0.15 * (Number(duration.value) / 60)
  const bloodAlcohol = Math.max(0, (0.8 * alcoholGrams) / (1.055 * totalBodyWater) - resorption)

  result.value = bloodAlcohol.toFixed(2).replace('.', ',')
  resultHidden.value = false

  EventBus.$emit('triggerAGF')
  adaSlotReloadOnInteraction(reloadConfig.value)
}

const validateData = (calculate: boolean) => {
  hasError.value = false
  errors.value = initErrors()
  resultHidden.value = true

  const validateFields: [ErrorField, InputValue][] = [
    ['gender', person.gender],
    ['age', person.age],
    ['height', person.height],
    ['weight', person.weight],
    ['beer', alcohol.beer],
    ['wine', alcohol.wine],
    ['liqueur', alcohol.liqueur],
    ['schnapps', alcohol.schnapps],
    ['duration', duration.value]
  ]

  let errorEmpty = false
  let errorFormat = false

  validateFields.forEach(([field, value]) => {
    if (requiredFields.includes(field) && value === '') {
      errors.value[field] = true
      errorEmpty = true
    } else if (field !== 'gender' && value !== '' && !isValidPositiveDecimalInput(value)) {
      errors.value[field] = true
      errorFormat = true
    }
  })

  hasError.value = errorEmpty || errorFormat
  errors.value.required = errorEmpty
  errors.value.number = errorFormat

  if (!hasError.value && calculate) calculateAlcohol()
}
</script>

<template>
  <div class="bx-content bx-content__continuous-text bx-typo--content-continuous-text">
    <form class="bx-form bx-typo--form bx-form--pur bx-typo--form-contact bx-form--alc-calc">
      <div class="bx-content__interline bx-typo--content-interline ">
        Angaben zur Person
      </div>
      <div class="bx-form__section bx-form__section--border">
        <div class="bx-form__select-wrapper">
          <label class="bx-form__label"
                 :class="{ 'bx-has-error': errors.gender }"
                 for="gender">
            Geschlecht
            <span class="bx-form__required"
                  title="Diese Angabe wird benötigt.">* </span>
          </label>
          <select id="gender"
                  v-model="person.gender"
                  class="bx-form__select"
                  :class="{ 'bx-has-error': errors.gender }">
            <option disabled
                    value="">
              Bitte wählen
            </option>
            <option value="male">
              Mann
            </option>
            <option value="female">
              Frau
            </option>
          </select>
          <Icon name="arrow_toggle" />
        </div>
        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.age }"
               for="person_age">
          Alter
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">* </span>
        </label>
        <input id="person_age"
               v-model="person.age"
               type="number"
               placeholder="in Jahren"
               :class="{ 'bx-has-error': errors.age }"
               @keydown="preventNonPositiveNumberKeys">

        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.height }"
               for="person_height">
          Größe
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">* </span>
        </label>
        <input id="person_height"
               v-model="person.height"
               placeholder="in cm"
               type="number"
               :class="{ 'bx-has-error': errors.height }"
               @keydown="preventNonPositiveNumberKeys">

        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.weight }"
               for="person_weight">
          Gewicht
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">* </span>
        </label>
        <input id="person_weight"
               v-model="person.weight"
               placeholder="in kg"
               type="number"
               :class="{ 'bx-has-error': errors.weight }"
               @keydown="preventNonPositiveNumberKeys">
      </div>

      <div class="bx-content__interline bx-typo--content-interline ">
        Konsumierte Alkoholmenge
      </div>

      <div class="bx-form__section bx-form__section--border">
        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.beer }"
               for="alcohol_beer">Bier (5% vol.)</label>
        <input id="alcohol_beer"
               v-model="alcohol.beer"
               placeholder="in l"
               :class="{ 'bx-has-error': errors.beer }"
               type="number"
               @keydown="preventNonPositiveNumberKeys">

        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.wine }"
               for="alcohol_wine">Wein (10% vol.)</label>
        <input id="alcohol_wine"
               v-model="alcohol.wine"
               placeholder="in l"
               :class="{ 'bx-has-error': errors.wine }"
               type="number"
               @keydown="preventNonPositiveNumberKeys">

        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.liqueur }"
               for="alcohol_liqueur">Likör (25% vol.)</label>
        <input id="alcohol_liqueur"
               v-model="alcohol.liqueur"
               placeholder="in cl"
               :class="{ 'bx-has-error': errors.liqueur }"
               type="number"
               @keydown="preventNonPositiveNumberKeys">

        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.schnapps }"
               for="alcohol_schnapps">Schnaps (40% vol.)</label>
        <input id="alcohol_schnapps"
               v-model="alcohol.schnapps"
               placeholder="in cl"
               :class="{ 'bx-has-error': errors.schnapps }"
               type="number"
               @keydown="preventNonPositiveNumberKeys">
      </div>

      <div class="bx-content__interline bx-typo--content-interline ">
        Zeitraum des Alkoholkonsums
      </div>

      <div class="bx-form__section">
        <label class="bx-form__label"
               :class="{ 'bx-has-error': errors.duration }"
               for="duration">
          Dauer
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">* </span>
        </label>
        <input id="duration"
               v-model="duration"
               placeholder="in Minuten"
               type="number"
               :class="{ 'bx-has-error': errors.duration }"
               @keydown="preventNonPositiveNumberKeys">
      </div>

      <div v-if="hasError"
           id="error-msg-wrapper"
           class="bx-has-error">
        <span v-if="errors.required"
              class="bx-form__message bx-form__message--error">
          Bitte alle Pflichtfelder ausfüllen.
        </span>
        <span v-if="errors.number"
              class="bx-form__message bx-form__message--error">
          Ungültige Zahleneingabe.
        </span>
      </div>

      <button id="calculate_alcohol"
              class="bx-form__button"
              @click.prevent="validateData(true)">
        Berechnen
      </button>

      <div class="bx-form__disclaimer-required">
        * Pflichtfelder
      </div>

      <div v-if="!resultHidden"
           class="bx-form__result">
        <div class="bx-content__interline bx-typo--content-interline ">
          Ergebnis: {{ result }} ‰
        </div>
      </div>
    </form>
  </div>
</template>
